import React, { createContext, useContext, useState, useEffect, useMemo, startTransition } from 'react';

const SiteSettingsContext = createContext({});

export const useSiteSettings = () => useContext(SiteSettingsContext);

export const SiteSettingsProvider = ({ children }) => {
  // A returning visitor must hydrate the same defaults as the server render.
  // Restore the cache after mount, without replacing pending Suspense content.
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (settings) return; // already have data from sessionStorage
    try {
      const cached = JSON.parse(sessionStorage.getItem('siteSettings'));
      if (cached && typeof cached === 'object' && !Array.isArray(cached)) {
        startTransition(() => { setSettings(cached); setLoading(false); });
        return;
      }
    } catch (e) { /* A malformed or unavailable cache falls back to the API. */ }
    // Delay fetch by 100ms to let LCP resources (hero image, critical CSS) claim bandwidth first
    const timer = setTimeout(async () => {
      try {
        const response = await fetch('/api/site-settings-api');
        if (response.ok) {
          const data = await response.json();
          const s = data.settings || {};
          // A fast API must not replace a still-hydrating lazy page/footer.
          startTransition(() => setSettings(s));
          try { sessionStorage.setItem('siteSettings', JSON.stringify(s)); } catch (e) { /* ignore */ }
        }
      } catch (error) {
        // silent fail - components use their own defaults
      } finally {
        startTransition(() => setLoading(false));
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [settings]);

  const value = useMemo(() => ({ settings: settings || {}, loading }), [settings, loading]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
